import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { DocumentContextService } from './document-context.service';
import { decodeJwtPayload } from '../utils/report-access-url.util';

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  organization: string;
  role: string;
  phone_number: string;
  is_verified: boolean;
  date_joined: string;
  profile: UserProfile;
}

export interface UserProfile {
  bio: string;
  timezone: string;
  language: string;
  email_notifications: boolean;
  sms_notifications: boolean;
  api_rate_limit: number;
  publisher_id: number | null;
}

export interface AuthTokens {
  access: string;
  refresh: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  password_confirm: string;
  organization?: string;
  role?: string;
  phone_number?: string;
  publisher_id?: number;
}

export interface AuthResponse {
  message: string;
  user: User;
  tokens: AuthTokens;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private documentContext = inject(DocumentContextService);

  private readonly API_URL = `${environment.apiUrl}/api/auth`;
  private readonly TOKEN_KEY = 'pra_access_token';
  private readonly REFRESH_TOKEN_KEY = 'pra_refresh_token';
  private readonly USER_KEY = 'pra_user';

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor() {
    this.loadUserFromStorage();
  }

  private loadUserFromStorage(): void {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const userStr = localStorage.getItem(this.USER_KEY);

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        this.currentUserSubject.next(user);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        this.logout();
      }
    }
  }

  register(request: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/register/`, request)
      .pipe(
        tap(response => this.handleAuthSuccess(response))
      );
  }

  login(request: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/login/`, request)
      .pipe(
        tap(response => this.handleAuthSuccess(response))
      );
  }

  logout(): Observable<any> {
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);

    // Clear local storage first
    this.clearAuthData();

    if (refreshToken) {
      return this.http.post(`${this.API_URL}/logout/`, { refresh_token: refreshToken })
        .pipe(
          tap(() => this.router.navigate(['/login']))
        );
    } else {
      this.router.navigate(['/login']);
      return new Observable(observer => observer.complete());
    }
  }

  refreshToken(): Observable<{ access: string }> {
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    return this.http.post<{ access: string }>(`${this.API_URL}/refresh/`, {
      refresh: refreshToken
    }).pipe(
      tap(response => {
        localStorage.setItem(this.TOKEN_KEY, response.access);
      })
    );
  }

  getProfile(): Observable<User> {
    return this.http.get<User>(`${this.API_URL}/profile/`);
  }

  updateProfile(profileData: Partial<User>): Observable<User> {
    return this.http.put<User>(`${this.API_URL}/profile/`, profileData)
      .pipe(
        tap(user => {
          localStorage.setItem(this.USER_KEY, JSON.stringify(user));
          this.currentUserSubject.next(user);
        })
      );
  }

  changePassword(passwordData: {
    old_password: string;
    new_password: string;
    new_password_confirm: string;
  }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_URL}/change-password/`, passwordData);
  }

  private handleAuthSuccess(response: AuthResponse): void {
    localStorage.setItem(this.TOKEN_KEY, response.tokens.access);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, response.tokens.refresh);
    localStorage.setItem(this.USER_KEY, JSON.stringify(response.user));
    this.currentUserSubject.next(response.user);
  }

  private clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.documentContext.clear();
    this.currentUserSubject.next(null);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token && !this.isTokenExpired(token);
  }

  private isTokenExpired(token: string): boolean {
    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload['exp'] !== 'number') {
      return true;
    }
    return Date.now() >= payload['exp'] * 1000; // exp is in seconds
  }

  hasRefreshToken(): boolean {
    return !!localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * A report share link authenticates with a standalone 12-hour token from
   * /api/auth/login-report/ and has no refresh token to fall back on.
   */
  isReportSession(): boolean {
    return !!this.getToken() && !this.hasRefreshToken();
  }

  /**
   * Starts a read-only session from an `?accessToken=` share link. Also pushes
   * the viewer onto currentUser$ - loadUserFromStorage() already ran when this
   * service was first injected, so writing localStorage alone leaves the app
   * rendering a null user until the next full page load.
   */
  setReportSession(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);

    const payload = decodeJwtPayload(token);
    if (!payload) {
      console.error('Unable to decode report access token payload.');
      return;
    }

    const user: User = {
      id: payload['user_id'],
      username: 'report-viewer',
      email: '',
      first_name: 'Report',
      last_name: 'Viewer',
      full_name: 'Report Viewer',
      organization: '',
      role: 'report_viewer',
      phone_number: '',
      is_verified: true,
      date_joined: '',
      profile: {
        bio: '',
        timezone: '',
        language: '',
        email_notifications: false,
        sms_notifications: false,
        api_rate_limit: 0,
        publisher_id: null
      }
    };

    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }
}